import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  useColorMode,
  useColorModeValue,
  Switch,
  FormControl,
  FormLabel,
  Select,
  Text,
  Input,
  Button,
  HStack,
  IconButton,
  useToast,
  Divider,
  Badge,
  useClipboard,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useLanguageStore } from "../stores/languageStore";
import { PrivateNavbar } from "./layout/PrivateNavbar";
import { Footer } from "./layout/Footer";
import {
  FiCopy,
  FiEye,
  FiEyeOff,
  FiKey,
  FiSave,
  FiTrash2,
} from "react-icons/fi";
import { userAPI } from "../services";
import { useNavigate } from "react-router-dom";
interface APIKey {
  provider: string;
  key: string;
}

// Add interface for user data
interface UserData {
  id: string;
  plan: string;
  billingPeriod: string;
}

export const Settings: React.FC = () => {
  const { colorMode, setColorMode } = useColorMode();
  const { t, i18n } = useTranslation();
  const { setLanguage } = useLanguageStore();
  const toast = useToast();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const keyItemBg = useColorModeValue("gray.50", "gray.700");
  const navigate = useNavigate();
  // State for API keys
  const [showKey, setShowKey] = useState<{ [key: string]: boolean }>({});
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [allowedDomains, setAllowedDomains] = useState<string>("");
  const [newKey, setNewKey] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("openai");
  const [arariaKey, setArariaKey] = useState("");
  const { hasCopied, onCopy } = useClipboard(arariaKey);
  const [userData, setUserData] = useState<UserData | null>(null);

  const handleThemeChange = (theme: string) => {
    userAPI.updatePreferences({ theme });
    setColorMode(theme);
  };
  // Add useEffect to fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const data = await userAPI.getCurrentUser();
        setUserData(data);
        setArariaKey(data.arariaApiKey);
        if (data.allowedDomains) {
          setAllowedDomains(data.allowedDomains.join(", "));
        }
      } catch (error) {
        toast({
          title: t("common.error"),
          description: t("settings.sections.plan.fetchError"),
          status: "error",
          duration: 3000,
        });
      }
    };
    fetchUserData();
  }, [toast, t]);

  const handleLanguageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newLanguage = event.target.value as "en" | "es-AR" | "pt-BR";
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
    userAPI.updatePreferences({ language: newLanguage });
  };

  const handleGenerateArariaKey = async () => {
    const response = await userAPI.generateArariaApiKey();
    setArariaKey(response.arariaApiKey);
    toast({
      title: t("settings.sections.api.araria.generate"),
      status: "success",
      duration: 3000,
    });
  };

  const handleSaveAPIKey = () => {
    if (!newKey.trim()) return;

    setApiKeys([...apiKeys, { provider: selectedProvider, key: newKey }]);
    setNewKey("");
    toast({
      title: t("settings.sections.api.providers.saved"),
      status: "success",
      duration: 3000,
    });
  };

  const handleDeleteAPIKey = (provider: string) => {
    setApiKeys(apiKeys.filter((key) => key.provider !== provider));
  };

  const toggleShowKey = (provider: string) => {
    setShowKey((prev) => ({ ...prev, [provider]: !prev[provider] }));
  };

  const handleSaveAllowedDomains = async () => {
    if (!allowedDomains) {
      await userAPI.updatePreferences({
        allowedDomains: [],
      });
      toast({
        title: t("settings.sections.api.araria.allowedDomainsSaved"),
        status: "success",
        duration: 3000,
      });
      return;
    }

    if (!allowedDomains.trim()) {
      toast({
        title: t("settings.sections.api.araria.error"),
        description: t("settings.sections.api.araria.emptyDomainsError"),
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Split domains by comma and clean up whitespace
    const allowedDomainsArray = allowedDomains.split(",").map((d) => d.trim());

    // Extract just the domain from each URL and validate
    const cleanedDomains = allowedDomainsArray.map((domain) => {
      // Remove protocol
      domain = domain.replace(/^https?:\/\//, "");

      // Get just the domain portion before any path/query/etc
      domain = domain.split("/")[0];

      return domain;
    });

    // Validate domains
    const invalidDomains = cleanedDomains.filter((domain) => {
      // Allow localhost with optional port
      if (/^localhost(:\d+)?$/.test(domain)) {
        return false;
      }

      // Check for valid domain format
      const domainRegex =
        /^([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(:\d+)?$/;
      return !domainRegex.test(domain);
    });

    if (invalidDomains.length > 0) {
      toast({
        title: t("settings.sections.api.araria.error"),
        description: `Invalid domains: ${invalidDomains.join(", ")}`,
        status: "error",
        duration: 3000,
      });
      return;
    }

    // Remove duplicates
    const uniqueDomains = Array.from(new Set(cleanedDomains));

    console.log("uniqueDomains", uniqueDomains);
    await userAPI.updatePreferences({
      allowedDomains: uniqueDomains,
    });

    toast({
      title: t("settings.sections.api.araria.allowedDomainsSaved"),
      status: "success",
      duration: 3000,
    });
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("settings.title")}
              </Heading>
              <Text color="gray.500">{t("settings.subtitle")}</Text>
            </VStack>

            {/* Site Settings Section */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              <Heading size="md">{t("settings.sections.site.title")}</Heading>
              <Text color="gray.500">
                {t("settings.sections.site.subtitle")}
              </Text>
              <Divider />

              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <FormLabel htmlFor="theme-toggle" mb="0">
                  {t("settings.darkMode")}
                </FormLabel>
                <Switch
                  id="theme-toggle"
                  isChecked={colorMode === "dark"}
                  onChange={() =>
                    handleThemeChange(colorMode === "dark" ? "light" : "dark")
                  }
                  colorScheme="brand"
                />
              </FormControl>

              <FormControl>
                <FormLabel htmlFor="language-select">
                  {t("settings.language")}
                </FormLabel>
                <Select
                  id="language-select"
                  value={i18n.language}
                  onChange={handleLanguageChange}
                  bg={cardBg}
                >
                  <option value="en">{t("settings.english")}</option>
                  <option value="pt-BR">{t("settings.portuguese")}</option>
                  <option value="es-AR">{t("settings.spanish")}</option>
                </Select>
              </FormControl>
            </VStack>

            {/* Plan Management Section */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              <Heading size="md">{t("settings.sections.plan.title")}</Heading>
              <Text color="gray.500">
                {t("settings.sections.plan.subtitle")}
              </Text>
              <Divider />

              <VStack spacing={4} align="stretch" w="full">
                <HStack justify="space-between">
                  <Text fontWeight="bold">
                    {t("settings.sections.plan.current")}:
                  </Text>
                  <Badge colorScheme="green" p={2} borderRadius="md">
                    {userData?.plan || "Loading..."}{" "}
                    {/* {userData?.billingPeriod && `(${userData.billingPeriod})`} */}
                  </Badge>
                </HStack>
                <Button
                  colorScheme="brand"
                  onClick={() => navigate("/pricing")}
                >
                  {t("settings.sections.plan.change")}
                </Button>
              </VStack>
            </VStack>
            {/* API Settings Section */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              <Heading size="md">{t("settings.sections.api.title")}</Heading>
              <Text color="gray.500">
                {t("settings.sections.api.subtitle")}
              </Text>
              <Divider />

              {/* Araria API Key */}
              <VStack spacing={4} align="stretch" w="full">
                <Heading size="sm">
                  {t("settings.sections.api.araria.title")}
                </Heading>
                <Text color="gray.500">
                  {t("settings.sections.api.araria.description")}
                </Text>
                <HStack>
                  <Input
                    value={arariaKey}
                    isReadOnly
                    type={showKey.araria ? "text" : "password"}
                    placeholder="••••••••••••••••"
                  />
                  <IconButton
                    aria-label="Toggle key visibility"
                    icon={showKey.araria ? <FiEyeOff /> : <FiEye />}
                    onClick={() => toggleShowKey("araria")}
                  />
                  <IconButton
                    aria-label="Copy key"
                    icon={<FiCopy />}
                    onClick={onCopy}
                  />
                </HStack>
                <Button
                  leftIcon={<FiKey />}
                  onClick={handleGenerateArariaKey}
                  colorScheme="brand"
                >
                  {arariaKey
                    ? t("settings.sections.api.araria.regenerate")
                    : t("settings.sections.api.araria.generate")}
                </Button>
                <Text color="gray.500">
                  {t("settings.sections.api.araria.allowedDomains")}
                </Text>
                <HStack>
                  <Input
                    value={allowedDomains}
                    onChange={(e) => setAllowedDomains(e.target.value)}
                    placeholder={t(
                      "settings.sections.api.araria.allowedDomainsPlaceholder"
                    )}
                  />
                  <IconButton
                    aria-label="Save allowed domains"
                    icon={<FiSave />}
                    onClick={handleSaveAllowedDomains}
                  />
                </HStack>
              </VStack>

              {/* <Divider /> */}

              {/* Provider API Keys */}
              <VStack spacing={4} align="stretch" w="full" display="none">
                <Heading size="sm">
                  {t("settings.sections.api.providers.title")}
                </Heading>
                <Text color="gray.500">
                  {t("settings.sections.api.providers.description")}
                </Text>
                {/* Add New Key */}
                <HStack>
                  <Select
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                    <option value="google">Google AI</option>
                  </Select>
                  <Input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder={t(
                      "settings.sections.api.providers.placeholder"
                    )}
                    type="password"
                  />

                  <IconButton
                    aria-label="Save API key"
                    icon={<FiSave />}
                    onClick={handleSaveAPIKey}
                  />
                </HStack>
                <Text as="i" color="gray.500" fontSize="sm">
                  {t("settings.sections.api.providers.notice")}
                </Text>
                {/* Saved Keys */}
                <Text>{JSON.stringify(userData?.plan)}</Text>
                <VStack spacing={2} align="stretch">
                  {apiKeys.map((apiKey) => (
                    <HStack
                      key={apiKey.provider}
                      justify="space-between"
                      p={2}
                      bg={keyItemBg}
                      borderRadius="md"
                    >
                      <HStack>
                        <Badge colorScheme="purple">{apiKey.provider}</Badge>
                        <Input
                          value={apiKey.key}
                          isReadOnly
                          type={showKey[apiKey.provider] ? "text" : "password"}
                          variant="unstyled"
                          width="auto"
                        />
                      </HStack>
                      <HStack>
                        <IconButton
                          aria-label="Toggle key visibility"
                          icon={
                            showKey[apiKey.provider] ? <FiEyeOff /> : <FiEye />
                          }
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleShowKey(apiKey.provider)}
                        />
                        <IconButton
                          aria-label="Delete key"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteAPIKey(apiKey.provider)}
                        />
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </VStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
